/**
 * Macro Generator Types
 * Types and interfaces for the MomentScience Macro Generator tool
 */

/**
 * Macro pattern types supported by MomentScience
 */
export enum MacroPattern {
  SIMPLE = 'SIMPLE',               // {{key}}
  FALLBACK = 'FALLBACK',           // {{key|'default'}}
  CONDITIONAL = 'CONDITIONAL',     // {{#if key}}...{{/if}}
  CONDITIONAL_ELSE = 'CONDITIONAL_ELSE',  // {{#if key}}...{{else}}...{{/if}}
  ADVANCED = 'ADVANCED'            // Complex combined patterns
}

/**
 * Build mode for creating macros
 */
export enum BuildMode {
  GUIDED = 'GUIDED',     // Guided builder with pattern selection
  ADVANCED = 'ADVANCED'  // Free-form syntax builder
}

/**
 * Individual macro configuration
 */
export interface Macro {
  id: string
  pattern: MacroPattern
  key: string
  fallbackValue?: string
  conditionalContent?: string
  elseContent?: string
  description?: string
  customSyntax?: string  // For advanced/combined patterns
  useFallbackInContent?: boolean  // Enable {{key|'default'}} in conditional content
}

/**
 * Pre-built macro template
 */
export interface MacroTemplate {
  id: string
  name: string
  description: string
  category: string
  macros: Omit<Macro, 'id'>[]
}

/**
 * Test data for macro preview
 */
export interface MacroTestData {
  [key: string]: string
}

/**
 * Saved macro session
 */
export interface MacroSession {
  macros: Macro[]
  testData: MacroTestData
  savedAt: string
}

/**
 * Macro validation result
 */
export interface MacroValidation {
  isValid: boolean
  errors: string[]
}

/**
 * Code export formats
 */
export enum ExportFormat {
  MACRO_SYNTAX = 'MACRO_SYNTAX',
  SDK_PAYLOAD = 'SDK_PAYLOAD',
  URL_PARAMS = 'URL_PARAMS'
}

/**
 * Message context for macro usage
 */
export enum MessageContext {
  HEADER_TEXT = 'HEADER_TEXT',
  LEAD_IN_TEXT = 'LEAD_IN_TEXT',
  TITLE_TEXT = 'TITLE_TEXT'
}

export enum Platform {
  SHOPIFY = 'SHOPIFY',
  MOMENTPERKS = 'MOMENTPERKS',
  PERKSWALL = 'PERKSWALL'
}

/**
 * User intent for handling missing data
 */
export type MissingDataIntent = 'none' | 'fallback' | 'conditional'

/**
 * Wizard state for step-by-step macro creation
 */
export interface WizardState {
  currentStep: number
  platform: Platform | null
  messageContext: MessageContext | null
  macroPattern: MacroPattern | null
  startWithTemplate: boolean
  selectedTemplate: string | null
  dataKey: string
  missingDataIntent: MissingDataIntent
  fallbackValue: string
  conditionalContent: string
  elseContent: string
  description: string
  advancedMode: boolean
  customSyntax: string  
  // Pattern-specific data storage
  patternData: {
    simple?: {
      dataKey: string
      description?: string
    }
    fallback?: {
      dataKey: string
      fallbackValue: string
      description?: string
    }
    conditional?: {
      dataKey: string
      conditionalContent: string
      description?: string
    }
    conditionalElse?: {
      dataKey: string
      conditionalContent: string
      elseContent: string
      description?: string
    }
    advanced?: {
      mode: 'guided' | 'custom'
      customSyntax: string
      description?: string
      dataKey?: string
      guidedConfig?: {
        dataKeys: string[]
        operators: any[]
        conditions: any[]
      }
    }
  }}

/**
 * Wizard step configuration
 */
export interface WizardStep {
  id: number
  title: string
  description: string
  canSkip?: boolean
  isOptional?: boolean
}
